import { Link, useForm, usePage } from '@inertiajs/react'
import { Button } from '@/shared/ui/button'
import { Label } from '@/shared/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'
import { Textarea } from '@/shared/ui/textarea'
import { NoAccessNotice } from '@/shared/ui/no-access-notice'
import { useCan } from '@/shared/hooks/useCan'
import { Resources, Actions } from '@/shared/rbac'
import { t } from '@/shared/lib/i18n'
import { instrumentPath, updateStatusInstrumentPath } from '@/shared/lib/routes'
import type { CurrentUserInfo } from '@/shared/types/CurrentUserInfo'

interface Props {
  instrumentId: number
  instrumentLabel: string
  currentStatusId: number | null
  statusOptions: { value: number; label: string }[]
  currentUserInfo: CurrentUserInfo | null
  flash: { notice?: string; alert?: string }
  [key: string]: unknown
}

const InstrumentStatusForm = () => {
  const { instrumentId, instrumentLabel, currentStatusId, statusOptions } = usePage<Props>().props

  const canSubmit = useCan(Resources.INSTRUMENT_STATUS, Actions.CREATE)

  const form = useForm({
    statusId: currentStatusId?.toString() ?? '',
    comment: '',
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    form.transform((data) => ({ statusUpdate: data }))
    form.patch(updateStatusInstrumentPath(instrumentId))
  }

  return (
    <div className="space-y-6">
      <div className="rounded-lg border p-6">
        <h1 className="text-2xl font-bold mb-2">{t('instruments.statusUpdate.pageTitle')}</h1>
        <p className="text-sm text-muted-foreground mb-6">{instrumentLabel}</p>

        <form onSubmit={handleSubmit} className="space-y-4 max-w-xl">
          <div className="space-y-1.5">
            <Label>{t('instruments.statusUpdate.statusField')} *</Label>
            <Select
              value={form.data.statusId}
              onValueChange={(value) => form.setData('statusId', value)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={t('instruments.statusUpdate.statusField')} />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value.toString()}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="comment">{t('instruments.statusUpdate.commentField')}</Label>
            <Textarea
              id="comment"
              value={form.data.comment}
              onChange={(e) => form.setData('comment', e.target.value)}
              maxLength={500}
              rows={3}
            />
          </div>

          {(form.errors as Record<string, string>).base && (
            <p className="text-sm text-destructive">
              {(form.errors as Record<string, string>).base}
            </p>
          )}

          {!canSubmit && <NoAccessNotice />}

          <div className="flex gap-3 pt-2">
            {canSubmit && (
              <Button type="submit" disabled={form.processing}>
                {t('instruments.statusUpdate.submit')}
              </Button>
            )}
            <Button asChild type="button" variant="outline">
              <Link href={instrumentPath(instrumentId)}>
                {t('instruments.statusUpdate.cancel')}
              </Link>
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default InstrumentStatusForm
